import { useTenderDeadline } from "../../../../hooks/useTenderDeadline";
import { useTenderActionError } from "../../../../hooks/useTenderActionError";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import style from "../common.module.scss";
import { Expeditor, Order } from "../../../../services";
import CustomButton from "../../../custom/custom-button/CustomButton";
import { toast } from "react-toastify";
import { TenderDeadlineText } from "../../../shared/TenderDeadlineText/TenderDeadlineText";
import CustomInputCurrency from "../../../custom/custom-input-currency/CustomInputCurrency";
import { CURRENCIES, WEIGHT_UNITS } from "../../../../mock";
import DeleteNewIcon from "../../../shared/icons/DeleteNewIcon"; 
import CommentIcon from "../../../shared/icons/CommentIcon";
import ArrowDownIcon from "../../../shared/icons/ArrowDownIcon";
import CustomInput from "../../../custom/custom-input/CustomInput";

const SuggestPrice = ({ onRefreshOrder, item, cargoDetails, onUpdateData, tenderOrderInfo }) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(item?.tenderDateEnd, { refreshKey: item });
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError(item, { onRefreshOrder });
  const [suggestPrice, setSuggestPrice] = useState("");
  const [suggestVolume, setSuggestVolume] = useState("");
  const [suggestWeight, setSuggestWeight] = useState("");
  const [suggestWeightUnit, setSuggestWeightUnit] = useState(() => ({ ...WEIGHT_UNITS(t).ton }));
  const [initialVolume, setInitialVolume] = useState("");
  const [initialWeight, setInitialWeight] = useState("");
  const [errorText, setErrorText] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [previousData, setPreviousData] = useState([]);
  const isTenderEnded = isTenderExpiredByServer;
  const [currency, setCurrency] = useState("KZT");
  const [comment, setComment] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [loadingRemoving, setLoadingRemoving] = useState(false);
  const [priceIdToDelete, setPriceIdToDelete] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState(null);

  const fetchSuggestedPrices = useCallback(() => {
    if (!item?.id) return;

    Expeditor.getOrderBidsByExpeditor(item.id)
      .then((response) => setPreviousData(response?.data))
      .catch((error) => console.log(error));
  }, [item?.id]);

  useEffect(() => {
    setPreviousData([]);
    if (!item) return;

    fetchSuggestedPrices();
  }, [item?.id, fetchSuggestedPrices]);


  useEffect(() => {
    if (!cargoDetails) return;
    setInitialVolume(cargoDetails.volume !== undefined && cargoDetails.volume !== null ? String(cargoDetails.volume) : "");
    setSuggestVolume(cargoDetails.volume !== undefined && cargoDetails.volume !== null ? String(cargoDetails.volume) : "");
    setInitialWeight(cargoDetails.weight !== undefined && cargoDetails.weight !== null ? String(cargoDetails.weight) : "");
    const unitValue = cargoDetails.unitMeasurement !== undefined && cargoDetails.unitMeasurement !== null ? String(cargoDetails.unitMeasurement) : "Kg";
    if(unitValue === "Ton") { 
      setSuggestWeightUnit(WEIGHT_UNITS(t).ton);
    }
    else if(unitValue === "Kg") {
      setSuggestWeightUnit(WEIGHT_UNITS(t).kg);
    }

  }, [cargoDetails]);

  const handleSuggestPrice = () => {
    const roundedPrice = Math.round(Number(suggestPrice));

    if (!roundedPrice) {
      setErrorText(t("orderInfo.suggestPrice.enterPriceError"));
      return;
    }
    if (roundedPrice <= 0) {
      setErrorText(t("orderInfo.suggestPrice.enterPositivePriceError"));
      return;
    }
    if (roundedPrice > 2000000000) {
      setErrorText(t("ordersCreate.validation.priceTooLargeError"));
      toast.error(t("ordersCreate.validation.priceTooLargeError"));
      return;
    }

    setLoading(true);
    setErrorText("");

    const data = {
      orderId: item.id,
      price: roundedPrice,
      currency: currency,
      comments: comment.trim(),
      volume: (suggestVolume === "" ? null : suggestVolume) !== "" ? Number(suggestVolume === "" ? initialVolume : suggestVolume) : null,
      weight: (suggestWeight === "" ? null : suggestWeight) !== "" ? Number(suggestWeight === "" ? initialWeight : suggestWeight) : null,
    };

    Expeditor.sendOrderBids(item.id, data)
      .then(() => {
        setLoading(false);
        toast.success(t("orderInfo.suggestPrice.suggestSuccess"));
        setComment("");
        setSuggestPrice("");
        setSuggestVolume("");
        setSuggestWeight("");
        fetchSuggestedPrices();
        onUpdateData();
      })
      .catch(async (error) => {
        setLoading(false);
        console.error("Ошибка при отправке цены:", error);
        setErrorText(t("orderInfo.suggestPrice.suggestError"));
        await requestErrorDisplay(error, t, "orderInfo.suggestPrice.suggestError");
      });
  };

  const handleConfirmDelete = async () => {
    if (!priceIdToDelete) return;
    try {
      setLoadingRemoving(true);
      await Expeditor.deleteOrderBid(priceIdToDelete);
      toast.success(t("orderInfo.suggestPrice.deleteSuccess"));
      setPreviousData((prev) => prev.filter((p) => p.id !== priceIdToDelete));
      onUpdateData();
      setPriceIdToDelete(null);
    } catch (error) {
      await requestErrorDisplay(error, t, "orderInfo.suggestPrice.deleteError");
      console.error("Ошибка при удалении:", error);
    } finally {
      setLoadingRemoving(false);
      setShowConfirmDelete(false);
    }
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    if (value.length <= 500) {
      setComment(value);
    }
  };

  const toggleComment = (priceId) => {
    setActiveCommentId((prevId) => (prevId === priceId ? null : priceId));
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={cn(style.cargo_wrapper)}>
      <div className={cn(style.cargo_title, style.orders_title)} style={{ gap: "6px", padding: "0" }}>
        <div className={cn(style.suggested_info)}>
          {item?.tenderDateEnd && (
            <div className={style.suggested_infoRow}>
              <span className={style.suggested_infoLabel}>{t("orderInfo.suggestPrice.title2")}</span>
              <span className={cn(style.suggested_infoValue, style.suggested_deadline)}>
                <TenderDeadlineText date={item.tenderDateEnd} />
              </span>
            </div>
          )}

          {previousData?.length > 0 && (
            <div className={style.suggested_infoRow}>
              <div className={style.suggested_priceList}>
                {previousData.length > 3 ? (
                  <>
                    <button onClick={toggleExpanded} className={style.suggested_priceToggle}>
                      <span>Ваши предыдущие ставки ({previousData.length})</span>
                      <ArrowDownIcon isExpanded={isExpanded} stroke="#000" />
                    </button>

                    {isExpanded && (
                      <div className={style.suggested_priceContainer}>
                        {previousData.map((priceItem) => (
                          <div key={priceItem.id}>
                            <div className={style.suggested_priceItem}>
                              <div className={style.suggested_priceLine}>
                                <span className={style.suggested_priceValue}>
                                  {priceItem.price} {CURRENCIES[priceItem.currency]?.symbol || priceItem.currency}
                                </span>
                                {priceItem.volume !== undefined && priceItem.volume !== null && (
                                  <span className={style.suggested_priceMeta}>
                                    {priceItem.volume} {t('ordersCreateDesktop.detailsSection.volumeUnit')}<sup>3</sup>
                                  </span>
                                )}
                                {priceItem.weight !== undefined && priceItem.weight !== null && (
                                  <span className={style.suggested_priceMeta}>
                                    {priceItem.weight} {cargoDetails?.unitMeasurement === "Kg" ? t("orderInfo.ordersInfoCargo.kgUnit") : t("orderInfo.ordersInfoCargo.tonsUnit")}
                                  </span>
                                )}
                              </div>
                              <div className={style.suggested_priceActions}>
                                {priceItem.comments && (
                                  <div
                                    onClick={() => toggleComment(priceItem.id)}
                                    className={style.suggested_iconButton}
                                  >
                                    <CommentIcon />
                                  </div>
                                )}
                                <div className={cn(style.suggested_iconButton, style.suggested_iconButton__delete)}>
                                  <DeleteNewIcon
                                    onClick={() => {
                                      setShowConfirmDelete(true);
                                      setPriceIdToDelete(priceItem.id);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                            {activeCommentId === priceItem.id && priceItem?.comments && (
                              <div className={style.suggested_comment}>{priceItem.comments}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className={style.suggested_priceContainer}>
                    {previousData.map((priceItem) => (
                      <div key={priceItem.id}>
                        <div className={style.suggested_priceItem}>
                          <div className={style.suggested_priceLine}>
                            <span className={style.suggested_priceValue}>
                              {priceItem.price} {CURRENCIES[priceItem.currency]?.symbol || priceItem.currency}
                            </span>
                            {priceItem.volume !== undefined && priceItem.volume !== null && (
                              <span className={style.suggested_priceMeta}>
                                {priceItem.volume} {t('ordersCreateDesktop.detailsSection.volumeUnit')}<sup>3</sup>
                              </span>
                            )}
                            {priceItem.weight !== undefined && priceItem.weight !== null && (
                              <span className={style.suggested_priceMeta}>
                                {priceItem.weight} {cargoDetails?.unitMeasurement === "Kg" ? t("orderInfo.ordersInfoCargo.kgUnit") : t("orderInfo.ordersInfoCargo.tonsUnit")}
                              </span>
                            )}
                          </div>
                          <div className={style.suggested_priceActions}>
                            {priceItem.comments && (
                              <div
                                onClick={() => toggleComment(priceItem.id)}
                                className={style.suggested_iconButton}
                              >
                                <CommentIcon />
                              </div>
                            )}
                            <div className={cn(style.suggested_iconButton, style.suggested_iconButton__delete)}>
                              <DeleteNewIcon
                                onClick={() => {
                                  setShowConfirmDelete(true);
                                  setPriceIdToDelete(priceItem.id);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        {activeCommentId === priceItem.id && priceItem?.comments && (
                          <div className={style.suggested_comment}>{priceItem.comments}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {!isTenderEnded && (
        <div className={style.form_section}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSuggestPrice();
            }}
          >
            <div className={style.form_group}>
              <div className={style.bidGridFormRow}>
                <div>
                  <CustomInputCurrency
                    value={suggestPrice}
                    isBgGray
                    inputType="number"
                    labelText={t("orderInfo.suggestPrice.yourPriceLabel") + "*"}
                    placeholder={t("orderInfo.suggestPrice.placeholder")}
                    errorText={errorText}
                    currency={currency}
                    onChangeValue={(value) => {
                      setSuggestPrice(value);
                      setErrorText("");
                    }}
                    onChangeCurrency={(currencyCode) => {
                      setCurrency(currencyCode);
                    }}
                  />
                </div> 

                <div>
                  <CustomInput
                    value={suggestVolume}
                    placeholder={initialVolume}
                    isBgGray
                    inputType="number"
                    labelText={t("ordersCreateDesktop.detailsSection.volumeLabel")}
                    prefixEnd=
                    {
                      <span>
                        {t("ordersCreateDesktop.detailsSection.volumeUnit")}<sup>3</sup>
                      </span>
                    }
                    onChangeValue={(volume) => {
                      setSuggestVolume(volume);
                    }}
                  />
                </div>
                <div>
                  <CustomInput
                    value={suggestWeight}
                    placeholder={initialWeight}
                    isBgGray
                    inputType="number"
                    labelText={t("ordersCreateDesktop.detailsSection.weightLabel")}
                    onChangeValue={(weight) => {
                      setSuggestWeight(weight);
                    }}
                    prefixEnd=
                    {
                      <span>
                       {suggestWeightUnit.code === "Kg" ? t("orderInfo.ordersInfoCargo.kgUnit") : t("orderInfo.ordersInfoCargo.tonsUnit")}
                      </span>
                    }
                  />
                </div>
              </div>
            </div>

            <div className={style.form_group}>
              <label className={style.form_label} htmlFor="comment">
                {t("orderInfo.suggestPrice.commentLabel", "Комментарий к предложению")}
                <span className={style.optional_label}>
                  {t("orderInfo.suggestPrice.optional", "(необязательно)")}
                </span>
              </label>
              <textarea
                id="comment"
                className={style.form_textarea}
                value={comment}
                onChange={handleCommentChange}
                placeholder={t(
                  "orderInfo.suggestPrice.commentPlaceholder",
                  "Укажите дополнительную информацию о вашем предложении, условиях поставки, скидках и т.д.",
                )}
                maxLength={500}
              />
              <div className={style.char_counter}>
                <span>{comment.length}</span> / 500
              </div>
            </div>

            <CustomButton
              text={t("orderInfo.suggestPrice.suggestButton")}
              style={{ flex: "1", width: "100%" }}
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
            />
          </form>
        </div>
      )}

      {showConfirmDelete && (
        <div className={style.confirmModalOverlay}>
          <div className={style.confirmModal}>
            <p>{t("orderInfo.suggestPrice.confirmDelete")}</p>
            <div className={style.confirmModal_actions}>
              <CustomButton
                isWhite
                text={t("common.cancel")}
                onClick={() => setShowConfirmDelete(false)}
              />
              <CustomButton
                text={t("common.confirm")}
                onClick={handleConfirmDelete}
                isLoading={loadingRemoving}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestPrice;
